// ── AskAiAboutVerseCard ───────────────────────────────────────────────────────
// Collapsible card that builds and dispatches a pre-filled AI prompt for the
// current verse (Verse Analysis) or the full poem (Poem Overview).
//
// Prompt type picker — first section in the open card:
//   "Poem Overview — One-Time Generation"
//   "Verse Analysis — Per Bayt"
//
// Clipboard behaviour:
//   - Auto-copies on card open, prompt type change, and language change.
//   - Auto-copies when "Open in [Provider]" is clicked (Gemini / URL-too-long path).
//   - "Copy prompt only →" link always available as a fallback.
//
// Paste-back: onPendingPaste(type) signals the coordinator which type to store.
// No session state lives here — runningSummary / poemOverview are passed as props.

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { buildPrompt, buildOverviewPrompt, buildProviderUrl } from '../lib/prompt-builder.js';
import SpotlightTour from './SpotlightTour.jsx';

// ── Constants ─────────────────────────────────────────────────────────────────

const PROVIDERS = [
  { id: 'claude',  label: 'Claude',  baseUrl: 'https://claude.ai/new',          note: 'Best quality for classical Arabic verse — handles balagha, Quranic register, and rhetorical devices most reliably. Requires a Claude account.' },
  { id: 'copilot', label: 'Copilot', baseUrl: 'https://copilot.microsoft.com/', recommended: true, note: 'Recommended — free with any Microsoft account. Opens with the full prompt pre-loaded, great for everyday analysis.' },
  { id: 'gemini',  label: 'Gemini',  baseUrl: 'https://gemini.google.com/app',  noUrlPrefill: true, note: 'Opens without a pre-filled prompt — your analysis is copied automatically. Paste it in once Gemini loads.' },
  { id: 'chatgpt', label: 'ChatGPT', baseUrl: 'https://chat.openai.com/',        note: 'Opens with the full prompt pre-loaded. Good general-purpose analysis.' },
];

const LANG_OPTIONS = [
  { id: 'en',   label: 'EN'   },
  { id: 'ar',   label: 'AR'   },
  { id: 'both', label: 'Both' },
];

const PROMPT_TYPES = [
  {
    id: 'overview',
    title: 'Poem Overview',
    subtitle: 'One-Time Generation',
    note: 'Generates a poet profile, occasion & context, thematic overview, plain reading of every verse, and a summary to paste back. Run this once before analysing individual verses.',
  },
  {
    id: 'verse',
    title: 'Verse Analysis',
    subtitle: 'Per Bayt',
    note: "Deep analysis of the current verse — plain meaning, balāgha, imagery, word roots, Qur'anic allusions, and running poem memory.",
  },
];

const QURAN_THRESHOLD = 0.5;

const DEV_URL_FLAG =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('devMode') === '1';

// ── Shared micro-components ───────────────────────────────────────────────────

function SectionLabel({ children, style }) {
  return (
    <div style={{
      fontFamily: "'Josefin Sans', sans-serif",
      fontSize: '0.58rem', letterSpacing: '0.15em',
      textTransform: 'uppercase', color: 'var(--text3)',
      marginBottom: '0.5rem', ...style,
    }}>
      {children}
    </div>
  );
}

function PillButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '0.3rem 0.7rem', borderRadius: 3,
        border: '1px solid',
        borderColor: active ? 'var(--em)' : 'var(--border)',
        background:  active ? 'var(--em-glow)' : 'transparent',
        color:       active ? 'var(--em)' : 'var(--text3)',
        fontFamily: "'Josefin Sans', sans-serif",
        fontSize: '0.62rem', letterSpacing: '0.1em',
        cursor: 'pointer', transition: 'border-color 0.1s, background 0.1s',
      }}
    >
      {children}
    </button>
  );
}

function PromptTypeButton({ active, onClick, title, subtitle }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.65rem',
        padding: '0.55rem 0.75rem', borderRadius: 4,
        border: '1px solid',
        borderColor: active ? 'var(--em)' : 'var(--border)',
        background:  active ? 'var(--em-glow)' : 'var(--bg3)',
        cursor: 'pointer', textAlign: 'left',
        transition: 'border-color 0.1s, background 0.1s',
        width: '100%',
      }}
    >
      <span style={{
        width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
        border: '1.5px solid',
        borderColor: active ? 'var(--em)' : 'var(--border)',
        background:  active ? 'var(--em)' : 'transparent',
        transition:  'background 0.1s, border-color 0.1s',
      }} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
        <span style={{
          fontFamily: "'Josefin Sans', sans-serif",
          fontSize: '0.65rem', letterSpacing: '0.1em',
          color: active ? 'var(--em)' : 'var(--text2)',
          fontWeight: active ? 600 : 400,
          transition: 'color 0.1s',
        }}>
          {title}
        </span>
        <span style={{
          fontFamily: "'Josefin Sans', sans-serif",
          fontSize: '0.55rem', letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: active ? 'var(--em)' : 'var(--text3)',
          opacity: active ? 0.8 : 1,
          transition: 'color 0.1s',
        }}>
          {subtitle}
        </span>
      </span>
    </button>
  );
}

function DevDetail({ label, children }) {
  return (
    <details>
      <summary style={{
        fontSize: '0.65rem', color: 'var(--text3)',
        cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif",
        letterSpacing: '0.08em', paddingBottom: '0.25rem',
      }}>
        {label}
      </summary>
      <pre style={{
        marginTop: '0.3rem', marginBottom: 0,
        fontSize: '0.6rem', color: 'var(--text2)',
        background: 'var(--bg3)', padding: '0.65rem',
        borderRadius: 4, overflowX: 'auto',
        whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.55,
      }}>
        {children}
      </pre>
    </details>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * @param {{
 *   poemId:               string,
 *   verseId:              string,
 *   verseText:            string,
 *   poemTitle:            string,
 *   poetName:             string,
 *   era:                  string,
 *   meter:                string,
 *   theme:                string,
 *   verseType:            string,
 *   quranicAllusionScore: number,
 *   poetSignature:        string,
 *   previousVerseText?:   string,
 *   nextVerseText?:       string,
 *   runningSummary:       string,
 *   poemOverview:         string,
 *   allVersesText:        string,
 *   historicalContext?:   string,
 *   verseIdx:             number,
 *   totalVerses:          number,
 *   onPendingPaste:       (type: 'verse' | 'overview') => void,
 * }} props
 */
export default function AskAiAboutVerseCard({
  poemId,
  verseId,
  verseText,
  poemTitle,
  poetName,
  era,
  meter,
  theme,
  verseType        = 'قصيدة كلاسيكية',
  quranicAllusionScore = 0,
  poetSignature,
  previousVerseText = '',
  nextVerseText     = '',
  runningSummary    = '',
  poemOverview      = '',
  allVersesText     = '',
  historicalContext = '',
  verseIdx          = 0,
  totalVerses       = 0,
  onPendingPaste,
  onOpen,
}) {
  // ── UI state ─────────────────────────────────────────────────────────────────
  const [open,        setOpen]        = useState(false);
  const [promptType,  setPromptType]  = useState('verse');  // 'verse' | 'overview'
  const [lang,        setLang]        = useState('en');
  const [provider,    setProvider]    = useState('copilot');
  const [devOpen,     setDevOpen]     = useState(DEV_URL_FLAG);
  const [copied,      setCopied]      = useState(false);
  const [opened,      setOpened]      = useState(false);
  const [showTour,    setShowTour]    = useState(false);

  // ── Tour refs ─────────────────────────────────────────────────────────────────
  const promptTypeRef = useRef(null);
  const langRef       = useRef(null);
  const providerRef   = useRef(null);
  const openBtnRef    = useRef(null);
  const pasteRef      = useRef(null);

  // ── Dev mode: triple-tap on the title ────────────────────────────────────────
  const tapCount = useRef(0);
  const tapTimer = useRef(null);

  const handleTitleTap = useCallback(() => {
    tapCount.current += 1;
    clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 800);
    if (tapCount.current >= 3) {
      tapCount.current = 0;
      setDevOpen(v => !v);
    }
  }, []);

  // ── Prompt options ────────────────────────────────────────────────────────────
  const versePromptOptions = useMemo(() => ({
    poemId,
    verseId,
    verseText,
    poemTitle,
    poetName,
    era,
    meter,
    theme,
    verseType,
    quranicAllusionScore,
    poetSignature: poetSignature || (poetName && era ? `${poetName} (${era})` : poetName),
    previousVerseText,
    nextVerseText,
    runningSummary,
    languageMode: lang,
    verseIdx,
    totalVerses,
  }), [
    poemId, verseId, verseText, poemTitle, poetName, era, meter,
    theme, verseType, quranicAllusionScore, poetSignature,
    previousVerseText, nextVerseText, runningSummary, lang,
    verseIdx, totalVerses,
  ]);

  const overviewPromptOptions = useMemo(() => ({
    allVersesText,
    poemTitle,
    poetName,
    era,
    meter,
    theme,
    verseType,
    poetSignature: poetSignature || (poetName && era ? `${poetName} (${era})` : poetName),
    historicalContext,
    languageMode: lang,
  }), [allVersesText, poemTitle, poetName, era, meter, theme, verseType, poetSignature, historicalContext, lang]);

  const { prompt: builtPrompt, tier, charCount } = useMemo(
    () => promptType === 'overview'
      ? buildOverviewPrompt(overviewPromptOptions)
      : buildPrompt(versePromptOptions),
    [promptType, overviewPromptOptions, versePromptOptions],
  );

  // ── Derived ───────────────────────────────────────────────────────────────────
  const selectedProvider = PROVIDERS.find(p => p.id === provider);
  const selectedType     = PROMPT_TYPES.find(t => t.id === promptType);
  const hasQuranicHint   = promptType === 'verse' && quranicAllusionScore >= QURAN_THRESHOLD;
  const providerUrl      = builtPrompt ? buildProviderUrl(provider, builtPrompt) : null;
  const urlTooLong       = providerUrl ? providerUrl.length > 6000 : false;
  const canOpen          = Boolean(providerUrl) && !urlTooLong;

  // ── Clipboard ─────────────────────────────────────────────────────────────────
  const handleCopyPrompt = useCallback(() => {
    if (!builtPrompt) return;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2500); };
    navigator.clipboard.writeText(builtPrompt).then(done).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = builtPrompt;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      done();
    });
  }, [builtPrompt]);

  // Auto-copy when the card opens, or when the prompt type or language changes while open.
  const prevOpen       = useRef(false);
  const prevPromptType = useRef(promptType);
  const prevLang       = useRef(lang);
  useEffect(() => {
    const justOpened  = !prevOpen.current && open;
    const typeChanged = prevPromptType.current !== promptType;
    const langChanged = prevLang.current !== lang;
    prevOpen.current       = open;
    prevPromptType.current = promptType;
    prevLang.current       = lang;
    if (open && (justOpened || typeChanged || langChanged)) {
      handleCopyPrompt();
    }
  }, [open, promptType, lang, handleCopyPrompt]);

  // ── Open-in-AI ────────────────────────────────────────────────────────────────
  const handleOpenInAI = useCallback(() => {
    const base = selectedProvider?.baseUrl;
    if (!selectedProvider?.noUrlPrefill && canOpen && providerUrl) {
      window.open(providerUrl, '_blank', 'noopener,noreferrer');
      setOpened(true);
      setTimeout(() => setOpened(false), 3000);
    } else {
      if (base) window.open(base, '_blank', 'noopener,noreferrer');
      handleCopyPrompt();
    }
    onPendingPaste(promptType);
  }, [canOpen, providerUrl, selectedProvider, onPendingPaste, handleCopyPrompt, promptType]);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{
      marginBottom: '1rem', border: '1px solid var(--border)',
      borderRadius: 4, overflow: 'hidden',
    }}>

      {/* ── Header ── */}
      <div
        role="button" tabIndex={0}
        onClick={() => setOpen(v => { if (!v && onOpen) onOpen(); return !v; })}
        onKeyDown={e => e.key === 'Enter' && setOpen(v => { if (!v && onOpen) onOpen(); return !v; })}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.75rem 1rem', background: 'var(--bg2)',
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <div onClick={handleTitleTap} style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span style={{
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: '0.65rem', letterSpacing: '0.14em',
              textTransform: 'uppercase', color: 'var(--em)',
            }}>
              Ask AI about this verse
            </span>
            {devOpen && (
              <span style={{
                background: 'rgba(239,100,80,0.1)',
                border: '1px solid rgba(239,100,80,0.3)',
                borderRadius: 3, padding: '0.1rem 0.35rem',
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.45rem', letterSpacing: '0.12em',
                textTransform: 'uppercase', color: 'rgba(239,100,80,0.75)',
                flexShrink: 0,
              }}>DEV</span>
            )}
          </div>
          {!open && (poemOverview || runningSummary) && (
            <div style={{
              marginTop: '0.2rem', fontSize: '0.64rem',
              color: 'var(--text3)', fontFamily: "'Josefin Sans', sans-serif",
            }}>
              {[
                poemOverview    && 'Overview saved',
                runningSummary  && 'Poem memory active',
              ].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        <span style={{
          color: 'var(--text3)', fontSize: '1.1rem', lineHeight: 1,
          transform: open ? 'rotate(90deg)' : 'none',
          transition: 'transform 0.15s',
        }}>›</span>
      </div>

      {open && (
        <div style={{
          padding: '1rem', background: 'var(--bg)',
          display: 'flex', flexDirection: 'column', gap: '1rem',
        }}>

          {/* ── Prompt type picker — always shown first ── */}
          <div ref={promptTypeRef}>
            <SectionLabel>Prompt type</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {PROMPT_TYPES.map(t => (
                <PromptTypeButton
                  key={t.id}
                  active={promptType === t.id}
                  onClick={() => setPromptType(t.id)}
                  title={t.title}
                  subtitle={t.subtitle}
                />
              ))}
            </div>
            {selectedType?.note && (
              <div style={{
                marginTop: '0.45rem',
                fontSize: '0.6rem', color: 'var(--text3)',
                fontFamily: "'Josefin Sans', sans-serif",
                lineHeight: 1.5,
                animation: 'fadeUp 0.15s ease',
              }}>
                {selectedType.note}
              </div>
            )}
          </div>

          {/* ── Memory indicators ── */}
          {runningSummary && promptType === 'verse' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.45rem 0.65rem',
              background: 'rgba(52,211,153,0.06)',
              border: '1px solid rgba(52,211,153,0.2)',
              borderRadius: 4,
            }}>
              <span style={{ fontSize: '0.7rem', lineHeight: 1 }}>✓</span>
              <span style={{
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.58rem', letterSpacing: '0.1em', color: '#34d399',
              }}>
                Poem memory loaded — previous verse context included in this prompt
              </span>
            </div>
          )}

          {/* ── How to use this ── */}
          <button
            onClick={() => setShowTour(true)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: '0.58rem', letterSpacing: '0.1em',
              textTransform: 'uppercase', color: 'var(--text3)',
              display: 'flex', alignItems: 'center', gap: '0.3rem',
            }}
          >
            <span style={{ fontSize: '0.75rem', lineHeight: 1 }}>?</span>
            How to use this
          </button>

          {/* ── Language toggle ── */}
          <div ref={langRef}>
            <SectionLabel>Output language</SectionLabel>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              {LANG_OPTIONS.map(opt => (
                <PillButton key={opt.id} active={lang === opt.id} onClick={() => setLang(opt.id)}>
                  {opt.label}
                </PillButton>
              ))}
            </div>
          </div>

          {/* ── Provider toggle ── */}
          <div ref={providerRef}>
            <SectionLabel>AI provider</SectionLabel>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {PROVIDERS.map(p => (
                <PillButton key={p.id} active={provider === p.id} onClick={() => setProvider(p.id)}>
                  {p.label}
                  {p.recommended && (
                    <span style={{
                      marginLeft: '0.3rem',
                      fontSize: '0.48rem', letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: provider === p.id ? 'var(--gold)' : 'var(--text3)',
                      opacity: provider === p.id ? 1 : 0.7,
                      verticalAlign: 'middle',
                    }}>★</span>
                  )}
                </PillButton>
              ))}
            </div>
            {selectedProvider?.note && (
              <div style={{
                marginTop: '0.45rem',
                fontSize: '0.6rem', color: 'var(--text3)',
                fontFamily: "'Josefin Sans', sans-serif",
                lineHeight: 1.5,
                animation: 'fadeUp 0.15s ease',
              }}>
                {selectedProvider.note}
              </div>
            )}
          </div>

          {/* ── Qur'anic allusion hint (verse analysis only) ── */}
          {hasQuranicHint && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
              padding: '0.6rem 0.75rem',
              background: 'rgba(212,168,83,0.07)',
              border: '1px solid rgba(212,168,83,0.22)',
              borderRadius: 4,
            }}>
              <span style={{ fontSize: '0.85rem', flexShrink: 0, lineHeight: 1.4 }}>📖</span>
              <span style={{ fontSize: '0.72rem', color: 'var(--text2)', lineHeight: 1.55 }}>
                This verse may contain a Qur'anic allusion. The AI will be prompted to identify the source and explain how it shapes the meaning.
              </span>
            </div>
          )}

          {/* ── Primary action ── */}
          <button
            ref={openBtnRef}
            onClick={handleOpenInAI}
            style={{
              width: '100%', padding: '0.78rem 1rem',
              background: 'var(--em-glow)', border: '1px solid var(--em)',
              borderRadius: 4, color: 'var(--em)',
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: '0.67rem', letterSpacing: '0.12em',
              textTransform: 'uppercase', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem',
            }}
          >
            Open in {selectedProvider?.label} <span style={{ fontSize: '0.9rem', lineHeight: 1 }}>↗</span>
          </button>

          {/* URL-prefill open confirmation */}
          {opened && !copied && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.4rem 0.65rem',
              background: 'rgba(140,207,225,0.08)',
              border: '1px solid rgba(140,207,225,0.25)',
              borderRadius: 4, marginTop: '-0.4rem',
              animation: 'fadeUp 0.2s ease',
            }}>
              <span style={{ fontSize: '0.7rem', color: 'var(--em)', lineHeight: 1 }}>✓</span>
              <span style={{
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.58rem', letterSpacing: '0.08em', color: 'var(--em)',
              }}>
                Opened {selectedProvider?.label} with prompt pre-loaded — no paste needed
              </span>
            </div>
          )}

          {/* Clipboard confirmation toast */}
          {copied && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.4rem 0.65rem',
              background: 'rgba(140,207,225,0.08)',
              border: '1px solid rgba(140,207,225,0.25)',
              borderRadius: 4, marginTop: '-0.4rem',
              animation: 'fadeUp 0.2s ease',
            }}>
              <span style={{ fontSize: '0.7rem', color: 'var(--em)', lineHeight: 1 }}>✓</span>
              <span style={{
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.58rem', letterSpacing: '0.08em', color: 'var(--em)',
              }}>
                Prompt copied to clipboard
              </span>
            </div>
          )}

          {/* Pre-action notes */}
          {!copied && !opened && selectedProvider?.noUrlPrefill && (
            <div style={{
              fontSize: '0.61rem', color: 'var(--text3)',
              fontFamily: "'Josefin Sans', sans-serif",
              lineHeight: 1.45, marginTop: '-0.5rem',
            }}>
              Gemini opens without a pre-filled prompt — your prompt is copied automatically. Paste it in once Gemini loads.
            </div>
          )}
          {!copied && !opened && !selectedProvider?.noUrlPrefill && urlTooLong && (
            <div style={{
              fontSize: '0.61rem', color: 'var(--text3)',
              fontFamily: "'Josefin Sans', sans-serif",
              lineHeight: 1.45, marginTop: '-0.5rem',
            }}>
              Prompt too long to pre-fill — copied to clipboard automatically. Paste into {selectedProvider?.label} once it opens.
            </div>
          )}

          {/* Copy fallback */}
          <div style={{ textAlign: 'center', marginTop: '-0.35rem' }}>
            <button
              onClick={handleCopyPrompt}
              style={{
                background: 'none', border: 'none', padding: '0.2rem 0',
                cursor: 'pointer',
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.57rem', letterSpacing: '0.1em',
                textTransform: 'uppercase', color: 'var(--text3)',
                textDecoration: 'underline',
                textDecorationColor: 'rgba(140,207,225,0.2)',
              }}
            >
              Copy prompt only →
            </button>
          </div>

          {/* Helper text */}
          <div style={{
            fontSize: '0.64rem', color: 'var(--text3)',
            textAlign: 'center', fontFamily: "'Josefin Sans', sans-serif",
            lineHeight: 1.5, marginTop: '-0.5rem',
          }}>
            {selectedProvider?.noUrlPrefill
              ? 'Opens Gemini — paste the copied prompt straight into the chat and hit send.'
              : 'Opens your chosen AI with the full prompt pre-loaded — paste your response back here when done.'}
          </div>

          {/* Section separator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', margin: '0.1rem 0' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border2)' }} />
            <span style={{
              fontSize: '0.52rem', letterSpacing: '0.16em',
              textTransform: 'uppercase', color: 'var(--text3)',
              fontFamily: "'Josefin Sans', sans-serif", flexShrink: 0,
            }}>then</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border2)' }} />
          </div>

          {/* ── Paste section ── */}
          <div ref={pasteRef} style={{
            display: 'flex', flexDirection: 'column', gap: '0.45rem',
            padding: '0.75rem 0.85rem',
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid var(--border2)',
            borderRadius: 6,
          }}>
            <div style={{
              fontSize: '0.59rem', letterSpacing: '0.12em',
              textTransform: 'uppercase', color: 'var(--text3)',
              fontFamily: "'Josefin Sans', sans-serif",
            }}>
              {promptType === 'overview' ? 'Paste your poem overview' : 'Paste your AI response'}
            </div>
            <div style={{
              fontSize: '0.62rem', color: 'var(--text3)',
              fontFamily: "'Josefin Sans', sans-serif", lineHeight: 1.5,
            }}>
              {promptType === 'overview'
                ? 'Copy the "Poem Overview to Paste Back:" block and paste it here to save it.'
                : 'After getting your analysis, paste it back here to save it to your poem memory.'}
            </div>
            <button
              onClick={() => onPendingPaste(promptType)}
              style={{
                width: '100%', padding: '0.6rem',
                background: 'none', border: '1px solid var(--border2)',
                borderRadius: 4, color: 'var(--text3)',
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: '0.62rem', letterSpacing: '0.1em',
                textTransform: 'uppercase', cursor: 'pointer',
              }}
            >
              {promptType === 'overview'
                ? 'Paste in your poem overview →'
                : 'Paste in your AI summary →'}
            </button>
          </div>

          {/* ── Developer mode ── */}
          {devOpen && (
            <div style={{
              borderTop: '1px solid var(--border2)', paddingTop: '0.85rem',
              display: 'flex', flexDirection: 'column', gap: '0.5rem',
            }}>
              <SectionLabel>Developer Mode</SectionLabel>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                {[
                  { label: 'Type',     value: promptType },
                  { label: 'Tier',     value: tier },
                  { label: 'Chars',    value: charCount },
                  { label: 'Lang',     value: lang },
                  { label: 'Provider', value: provider },
                ].map(({ label: l, value: v }) => (
                  <div key={l} style={{ fontSize: '0.62rem', fontFamily: "'Josefin Sans', sans-serif" }}>
                    <span style={{ color: 'var(--text3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{l} </span>
                    <span style={{ color: 'var(--em)', fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
              </div>
              <DevDetail label="Built Prompt">{builtPrompt}</DevDetail>
              <DevDetail label="Provider URL (decoded)">
                {(() => {
                  const url = buildProviderUrl(provider, builtPrompt);
                  return url ? decodeURIComponent(url) : '(unknown provider — use Copy Prompt)';
                })()}
              </DevDetail>
              <DevDetail label="Provider URL (encoded)">
                {buildProviderUrl(provider, builtPrompt) ?? '(unknown provider)'}
              </DevDetail>
              <DevDetail label="Prompt Context">
                {JSON.stringify({
                  promptType, poemId, verseId,
                  poemOverviewSample: poemOverview
                    ? poemOverview.slice(0, 80) + (poemOverview.length > 80 ? '…' : '')
                    : null,
                  runningSummarySample: runningSummary
                    ? runningSummary.slice(0, 100) + (runningSummary.length > 100 ? '…' : '')
                    : null,
                  allVersesLen: allVersesText.length,
                  quranicAllusionScore,
                  languageMode: lang,
                }, null, 2)}
              </DevDetail>
            </div>
          )}
        </div>
      )}

      {/* ── Mini spotlight tour ── */}
      {showTour && (
        <SpotlightTour
          steps={[
            {
              ref: promptTypeRef,
              title: 'Choose your prompt type',
              desc: 'Start with "Poem Overview" to get a one-line summary of what the poem is about. Then switch to "Verse Analysis" to go through each bayt in depth.',
            },
            {
              ref: langRef,
              title: 'Choose your output language',
              desc: "EN gives a full English analysis. AR responds in Arabic throughout. Both delivers each section in English then Arabic side-by-side.",
            },
            {
              ref: providerRef,
              title: 'Choose your AI',
              desc: "Copilot is our recommended pick — free with any Microsoft account and opens with the prompt pre-loaded. Claude gives the highest-quality analysis of classical Arabic verse if you have an account.",
            },
            {
              ref: openBtnRef,
              title: 'Open in your chosen AI',
              desc: "One tap opens the AI with the full prompt pre-loaded — the verse, poem metadata, and any memory from earlier verses.",
            },
            {
              ref: pasteRef,
              title: 'Paste your AI response back',
              desc: 'Copy the "Poem Overview to Paste Back:" or "Summary to Paste Back:" block from the AI\'s reply and tap this button. The app saves it — every subsequent verse prompt automatically includes the context you built up.',
              ctaLabel: 'Paste response',
              ctaAction: () => onPendingPaste(promptType),
            },
          ]}
          onDone={() => setShowTour(false)}
        />
      )}
    </div>
  );
}
